/**
 * CloudFront Authentication Utilities
 * 
 * Provides utilities for validating and managing CloudFront signed cookies
 * without making assumptions based on HTTP error codes.
 */

export interface CloudFrontCookies {
  policy?: string;
  signature?: string;
  keyPairId?: string;
}

export interface CloudFrontValidationResult {
  isValid: boolean;
  hasAllCookies: boolean;
  isExpired: boolean;
  expirationTime?: Date;
  missingCookies: string[];
  details: string;
}

/**
 * Extract CloudFront cookies from document.cookie
 */
export function getCloudFrontCookies(): CloudFrontCookies {
  const cookies: CloudFrontCookies = {};
  
  if (typeof document === 'undefined') {
    return cookies; // SSR safety
  }
  
  const cookieString = document.cookie;
  const cookiePairs = cookieString.split(';');
  
  for (const pair of cookiePairs) {
    const [name, value] = pair.trim().split('=');
    
    switch (name) {
      case 'CloudFront-Policy':
        cookies.policy = value;
        break;
      case 'CloudFront-Signature':
        cookies.signature = value;
        break;
      case 'CloudFront-Key-Pair-Id':
        cookies.keyPairId = value;
        break;
    }
  }
  
  return cookies;
}

/**
 * Validate CloudFront cookies without making network requests
 */
export function validateCloudFrontCookies(): CloudFrontValidationResult {
  const cookies = getCloudFrontCookies();
  const requiredCookies = ['policy', 'signature', 'keyPairId'];
  const missingCookies: string[] = [];
  
  // Check which cookies are missing
  for (const cookieName of requiredCookies) {
    if (!cookies[cookieName as keyof CloudFrontCookies]) {
      missingCookies.push(`CloudFront-${cookieName.charAt(0).toUpperCase() + cookieName.slice(1)}`);
    }
  }
  
  const hasAllCookies = missingCookies.length === 0;
  let isExpired = false;
  let expirationTime: Date | undefined;
  let policyDecodingFailed = false;
  
  // Try to decode the policy to check expiration (CloudFront policies are base64 encoded JSON)
  if (cookies.policy) {
    try {
      // Validate that the policy looks like base64 before attempting to decode
      const policyValue = cookies.policy.trim();
      if (!policyValue || policyValue.length === 0) {
        policyDecodingFailed = true;
      } else {
        // CloudFront policy is base64url encoded, need to convert to regular base64
        const base64Policy = policyValue.replace(/-/g, '+').replace(/_/g, '/');
        // Add padding if needed
        const paddedPolicy = base64Policy + '=='.substring(0, (4 - base64Policy.length % 4) % 4);
        
        // Validate base64 format before attempting to decode
        if (!/^[A-Za-z0-9+/]*={0,2}$/.test(paddedPolicy)) {
          policyDecodingFailed = true;
        } else {
          const decodedPolicy = atob(paddedPolicy);
          
          // Additional validation: check if decoded policy looks like JSON
          if (!decodedPolicy.trim().startsWith('{')) {
            policyDecodingFailed = true;
          } else {
            const policyObj = JSON.parse(decodedPolicy);
            
            // CloudFront policy structure: {"Statement":[{"Resource":"...","Condition":{"DateLessThan":{"AWS:EpochTime":timestamp}}}]}
            const statement = policyObj.Statement?.[0];
            const condition = statement?.Condition?.DateLessThan;
            const epochTime = condition?.['AWS:EpochTime'];
            
            if (epochTime) {
              expirationTime = new Date(epochTime * 1000); // Convert from epoch seconds to milliseconds
              isExpired = expirationTime < new Date();
            }
          }
        }
      }
    } catch (error) {
      // Only log CloudFront policy errors in development mode for security
      if (process.env.NODE_ENV === 'development' && typeof console.warn === 'function') {
        console.warn('[CloudFrontAuth] Failed to decode CloudFront policy for expiration check:', error);
      }
      policyDecodingFailed = true;
    }
  }
  
  // Policy is invalid if we can't decode it or if it's expired
  const isValid = hasAllCookies && !isExpired && !policyDecodingFailed;
  
  let details = '';
  if (!hasAllCookies) {
    details += `Missing cookies: ${missingCookies.join(', ')}. `;
  }
  if (policyDecodingFailed) {
    details += `Policy is malformed or corrupted. `;
  }
  if (isExpired && expirationTime) {
    details += `Policy expired at ${expirationTime.toISOString()}. `;
  }
  if (isValid) {
    details = `All cookies present${expirationTime ? ` and valid until ${expirationTime.toISOString()}` : ''}`;
  }
  
  return {
    isValid,
    hasAllCookies,
    isExpired,
    expirationTime,
    missingCookies,
    details: details.trim()
  };
}

/**
 * Check if a 403 error is likely due to CloudFront authentication issues
 * This is more reliable than just checking for 403 status codes
 */
export function isCloudFrontAuthError(errorResponse: Response, errorText?: string): boolean {
  if (errorResponse.status !== 403) {
    return false;
  }
  
  // Check for CloudFront-specific error indicators in the response
  const cloudFrontHeaders = [
    'x-amz-cf-pop',
    'x-amz-cf-id',
    'x-cache'
  ];
  
  const hasCloudFrontHeaders = cloudFrontHeaders.some(header => 
    errorResponse.headers.get(header)
  );
  
  // Check for CloudFront-specific error messages
  const cloudFrontErrorIndicators = [
    'AccessDenied',
    'MissingKey', 
    'InvalidSignature',
    'Policy expired',
    'SignatureDoesNotMatch',
    'InvalidKeyPairId'
  ];
  
  const hasCloudFrontErrorMessage = errorText ? 
    cloudFrontErrorIndicators.some(indicator => errorText.includes(indicator)) : 
    false;
  
  return hasCloudFrontHeaders || hasCloudFrontErrorMessage;
}

/**
 * Test CloudFront authentication by making a minimal request to a known CDN endpoint
 * This is more reliable than waiting for layer failures
 */
export async function testCloudFrontAuth(testUrl?: string): Promise<{
  isWorking: boolean;
  statusCode?: number;
  error?: string;
  cookieValidation: CloudFrontValidationResult;
}> {
  const cookieValidation = validateCloudFrontCookies();
  
  // If cookies are clearly invalid, don't bother with network test
  if (!cookieValidation.hasAllCookies || cookieValidation.isExpired) {
    return {
      isWorking: false,
      error: cookieValidation.details,
      cookieValidation
    };
  }
  
  // If no test URL provided, just return cookie validation
  if (!testUrl) {
    return {
      isWorking: cookieValidation.isValid,
      cookieValidation
    };
  }
  
  try {
    const response = await fetch(testUrl, {
      method: 'HEAD', // Use HEAD to minimize data transfer
      credentials: 'include',
      cache: 'no-cache'
    });
    
    return {
      isWorking: response.ok,
      statusCode: response.status,
      error: response.ok ? undefined : `HTTP ${response.status}: ${response.statusText}`,
      cookieValidation
    };
  } catch (error) {
    return {
      isWorking: false,
      error: error instanceof Error ? error.message : 'Network error',
      cookieValidation
    };
  }
}

/**
 * Debug function to log current CloudFront authentication status
 * Useful for troubleshooting authentication issues
 * Only logs in development mode for security
 */
export function debugCloudFrontAuth(): void {
  // Only debug in development mode to prevent logging sensitive info in production
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  
  const validation = validateCloudFrontCookies();
  const cookies = getCloudFrontCookies();
  
  console.group('🔐 CloudFront Authentication Status');
  console.log('Overall Status:', validation.isValid ? '✅ Valid' : '❌ Invalid');
  console.log('Details:', validation.details);
  console.log('Has All Cookies:', validation.hasAllCookies);
  console.log('Is Expired:', validation.isExpired);
  if (validation.expirationTime) {
    console.log('Expiration Time:', validation.expirationTime.toISOString());
    console.log('Time Until Expiry:', Math.round((validation.expirationTime.getTime() - Date.now()) / 1000 / 60), 'minutes');
  }
  console.log('Missing Cookies:', validation.missingCookies);
  console.log('Raw Cookies:', {
    policy: cookies.policy ? `${cookies.policy.substring(0, 50)}...` : 'missing',
    signature: cookies.signature ? `${cookies.signature.substring(0, 50)}...` : 'missing',
    keyPairId: cookies.keyPairId || 'missing'
  });
  console.groupEnd();
}

/**
 * Test function to simulate different CloudFront cookie scenarios
 * Useful for development and testing
 */
export function testCloudFrontCookieScenarios(): void {
  console.group('🧪 Testing CloudFront Cookie Scenarios');
  
  // Save current cookies
  const originalCookies = document.cookie;
  
  // Test 1: No cookies
  console.log('\n📋 Test 1: No CloudFront cookies');
  clearCloudFrontCookies();
  debugCloudFrontAuth();
  
  // Test 2: Missing signature cookie
  console.log('\n📋 Test 2: Missing signature cookie');
  const testPolicy = btoa(JSON.stringify({
    "Statement": [{
      "Resource": "https://cdn-dev.culturemap.app/*",
      "Condition": {
        "DateLessThan": {
          "AWS:EpochTime": Math.floor(Date.now() / 1000) + 1800 // 30 minutes from now
        }
      }
    }]
  }));
  document.cookie = `CloudFront-Policy=${testPolicy}; path=/`;
  document.cookie = 'CloudFront-Key-Pair-Id=APKAI123456789; path=/';
  debugCloudFrontAuth();
  
  // Test 3: Expired policy (past date)
  console.log('\n📋 Test 3: Expired policy');
  const expiredPolicy = btoa(JSON.stringify({
    "Statement": [{
      "Resource": "https://cdn-dev.culturemap.app/*",
      "Condition": {
        "DateLessThan": {
          "AWS:EpochTime": Math.floor(Date.now() / 1000) - 3600 // 1 hour ago
        }
      }
    }]
  }));
  document.cookie = `CloudFront-Policy=${expiredPolicy}; path=/`;
  document.cookie = 'CloudFront-Signature=fake-signature-123; path=/';
  document.cookie = 'CloudFront-Key-Pair-Id=APKAI123456789; path=/';
  debugCloudFrontAuth();
  
  // Test 4: Valid policy (future date)
  console.log('\n📋 Test 4: Valid policy (future expiration)');
  const validPolicy = btoa(JSON.stringify({
    "Statement": [{
      "Resource": "https://cdn-dev.culturemap.app/*",
      "Condition": {
        "DateLessThan": {
          "AWS:EpochTime": Math.floor(Date.now() / 1000) + 3600 // 1 hour from now
        }
      }
    }]
  }));
  document.cookie = `CloudFront-Policy=${validPolicy}; path=/`;
  document.cookie = 'CloudFront-Signature=fake-signature-456; path=/';
  document.cookie = 'CloudFront-Key-Pair-Id=APKAI123456789; path=/';
  debugCloudFrontAuth();
  
  // Test 5: Malformed policy
  console.log('\n📋 Test 5: Malformed policy');
  document.cookie = 'CloudFront-Policy=invalid-base64-data; path=/';
  document.cookie = 'CloudFront-Signature=fake-signature-789; path=/';
  document.cookie = 'CloudFront-Key-Pair-Id=APKAI123456789; path=/';
  debugCloudFrontAuth();
  
  // Restore original cookies (clear test cookies)
  clearCloudFrontCookies();
  
  console.log('\n✅ Test completed - cookies cleared');
  console.groupEnd();
}

/**
 * Quick test to check if CloudFront error detection works
 */
export function testCloudFrontErrorDetection(): void {
  console.group('🧪 Testing CloudFront Error Detection');
  
  // Create mock responses
  const mockCloudFrontResponse = new Response('AccessDenied', {
    status: 403,
    statusText: 'Forbidden',
    headers: new Headers({
      'x-amz-cf-pop': 'SYD1-C1',
      'x-amz-cf-id': 'test-request-id',
      'x-cache': 'Error from cloudfront'
    })
  });
  
  const mockServerResponse = new Response('Access denied', {
    status: 403,
    statusText: 'Forbidden',
    headers: new Headers({
      'server': 'nginx/1.18.0'
    })
  });
  
  console.log('CloudFront 403 with headers:', isCloudFrontAuthError(mockCloudFrontResponse, 'AccessDenied'));
  console.log('Server 403 without CloudFront headers:', isCloudFrontAuthError(mockServerResponse, 'Access denied'));
  console.log('CloudFront 403 with error text only:', isCloudFrontAuthError(mockServerResponse, 'MissingKey: Policy missing'));
  
  console.groupEnd();
}

/**
 * Clear CloudFront cookies (same as in UserContext but centralized)
 */
export function clearCloudFrontCookies(): void {
  const cookies = ['CloudFront-Policy', 'CloudFront-Signature', 'CloudFront-Key-Pair-Id'];
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
  
  // Clear for current domain
  cookies.forEach(cookie => {
    document.cookie = `${cookie}=; ${expiry}; path=/;`;
  });
  
  // Clear for parent domain (e.g., .culturemap.app)
  const hostname = window.location.hostname;
  if (hostname.includes('.')) {
    const domain = hostname.split('.').slice(-2).join('.');
    cookies.forEach(cookie => {
      document.cookie = `${cookie}=; ${expiry}; path=/; domain=.${domain};`;
    });
  }
}

/**
 * Check for and clear corrupted CloudFront cookies
 * Useful when users are experiencing authentication issues due to malformed cookies
 */
export function clearCorruptedCloudFrontCookies(): boolean {
  const validation = validateCloudFrontCookies();
  
  // If validation fails due to policy decoding issues, clear the cookies
  if (validation.details.includes('malformed') || validation.details.includes('corrupted')) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[CloudFrontAuth] Detected corrupted cookies, clearing them...');
    }
    clearCloudFrontCookies();
    return true; // Indicates that cookies were cleared
  }
  
  return false; // No action needed
}

// Make test functions available globally for browser console testing
if (typeof window !== 'undefined') {
  (window as any).testCloudFrontCookieScenarios = testCloudFrontCookieScenarios;
  (window as any).testCloudFrontErrorDetection = testCloudFrontErrorDetection;
  (window as any).debugCloudFrontAuth = debugCloudFrontAuth;
  (window as any).validateCloudFrontCookies = validateCloudFrontCookies;
  (window as any).clearCloudFrontCookies = clearCloudFrontCookies;
  (window as any).clearCorruptedCloudFrontCookies = clearCorruptedCloudFrontCookies;
}
